(() => {
  'use strict';

  const ROOT_SELECTOR =
    '[data-cn-homepage-popular-cards]';

  const TRACK_SELECTOR =
    '[data-cn-popular-track]';

  const initialized = new WeakSet();
  const requestCache = new Map();
  const trackStates = new WeakMap();

  function shuffle(items) {
    const copy = [...items];

    for (
      let index = copy.length - 1;
      index > 0;
      index -= 1
    ) {
      const randomIndex =
        Math.floor(Math.random() * (index + 1));

      [copy[index], copy[randomIndex]] = [
        copy[randomIndex],
        copy[index]
      ];
    }

    return copy;
  }

  function setBooleanAttribute(
    element,
    name,
    value
  ) {
    const next = value ? 'true' : 'false';

    if (element.getAttribute(name) !== next) {
      element.setAttribute(name, next);
    }
  }

  function measureScrollState(root, track) {
    const maxScroll = Math.max(
      0,
      track.scrollWidth - track.clientWidth
    );

    const current = Math.min(
      maxScroll,
      Math.max(0, track.scrollLeft)
    );

    const threshold = 2;

    return {
      root,
      canScrollLeft:
        maxScroll > threshold &&
        current > threshold,
      canScrollRight:
        maxScroll > threshold &&
        current < maxScroll - threshold
    };
  }

  function applyScrollState(state) {
    setBooleanAttribute(
      state.root,
      'data-can-scroll-left',
      state.canScrollLeft
    );

    setBooleanAttribute(
      state.root,
      'data-can-scroll-right',
      state.canScrollRight
    );
  }

  function scheduleScrollState(root, track) {
    const state = trackStates.get(track);

    if (!state || state.frame) return;

    state.frame = requestAnimationFrame(() => {
      state.frame = 0;

      /*
        Erst alle Geometriewerte lesen, danach Attribute schreiben.
        So entsteht kein Read/Write/Read-Wechsel.
      */
      const measurement =
        measureScrollState(root, track);

      applyScrollState(measurement);
    });
  }

  function buildPageUrl(
    collectionUrl,
    pageNumber
  ) {
    const url = new URL(
      collectionUrl,
      window.location.origin
    );

    url.searchParams.set(
      'page',
      String(pageNumber)
    );

    return url.toString();
  }

  function getCacheKey(url) {
    return `cn-popular-page:${url}`;
  }

  function readSessionCards(url) {
    try {
      const raw = sessionStorage.getItem(
        getCacheKey(url)
      );

      return raw ? JSON.parse(raw) : null;
    } catch (_error) {
      return null;
    }
  }

  function writeSessionCards(url, cards) {
    try {
      sessionStorage.setItem(
        getCacheKey(url),
        JSON.stringify(cards)
      );
    } catch (_error) {}
  }

  function extractCardHtml(html) {
    const parsed =
      new DOMParser().parseFromString(
        html,
        'text/html'
      );

    return Array.from(
      parsed.querySelectorAll(
        '#product-grid > .grid__item'
      )
    ).map((item) => item.outerHTML);
  }

  async function fetchPageCards(
    collectionUrl,
    pageNumber
  ) {
    const url = buildPageUrl(
      collectionUrl,
      pageNumber
    );

    if (requestCache.has(url)) {
      return requestCache.get(url);
    }

    const cached = readSessionCards(url);

    if (cached) {
      const request = Promise.resolve(cached);
      requestCache.set(url, request);
      return request;
    }

    const request = fetch(url, {
      credentials: 'same-origin',
      priority: 'low',
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(
            `Collection page ${pageNumber} failed`
          );
        }

        return response.text();
      })
      .then((html) => {
        const cards = extractCardHtml(html);
        writeSessionCards(url, cards);
        return cards;
      })
      .catch((error) => {
        requestCache.delete(url);
        throw error;
      });

    requestCache.set(url, request);
    return request;
  }

  function choosePages(maxPage, amount) {
    const selected = new Set();
    const bucketSize = maxPage / amount;

    for (
      let index = 0;
      index < amount;
      index += 1
    ) {
      const start =
        Math.floor(index * bucketSize) + 1;

      const end = Math.max(
        start,
        Math.floor((index + 1) * bucketSize)
      );

      selected.add(
        Math.min(
          maxPage,
          start +
            Math.floor(
              Math.random() *
                (end - start + 1)
            )
        )
      );
    }

    return shuffle([...selected]);
  }

  function getProductKey(cardHtml) {
    const template =
      document.createElement('template');

    template.innerHTML = cardHtml.trim();

    return (
      template.content
        .querySelector(
          '.card__heading a[href*="/products/"]'
        )
        ?.getAttribute('href') || ''
    );
  }

  function chooseCards(groups, displayCount) {
    const shuffledGroups = groups
      .filter((cards) => Array.isArray(cards) && cards.length)
      .map((cards) => shuffle(cards));

    const result = [];
    const seen = new Set();

    let index = 0;
    let added = true;

    while (
      result.length < displayCount &&
      added
    ) {
      added = false;

      for (const cards of shuffledGroups) {
        const cardHtml = cards[index];
        if (!cardHtml) continue;

        const key = getProductKey(cardHtml);

        if (!key || seen.has(key)) continue;

        seen.add(key);
        result.push(cardHtml);
        added = true;

        if (result.length >= displayCount) {
          break;
        }
      }

      index += 1;
    }

    return shuffle(result).slice(
      0,
      displayCount
    );
  }

  function createSlide(cardHtml) {
    const template =
      document.createElement('template');

    template.innerHTML = cardHtml.trim();

    const gridItem =
      template.content.firstElementChild;

    const slide = document.createElement('li');

    slide.className =
      'cn-homepage-popular-cards__slide grid__item';

    const card = gridItem?.querySelector(
      '.card-wrapper.product-card-wrapper'
    );

    if (card) {
      slide.appendChild(
        card.cloneNode(true)
      );
    } else if (gridItem) {
      slide.replaceChildren(
        ...gridItem.childNodes
      );
    }

    return slide;
  }

  async function loadRandomCards(root) {
    const collectionUrl =
      root.dataset.collectionUrl;

    if (!collectionUrl) return [];

    const productCount = Math.max(
      1,
      Number.parseInt(
        root.dataset.productCount || '1',
        10
      )
    );

    const displayCount = Math.max(
      1,
      Number.parseInt(
        root.dataset.displayCount || '10',
        10
      )
    );

    const productsPerPage = Math.max(
      1,
      Number.parseInt(
        root.dataset.productsPerPage || '16',
        10
      )
    );

    const maxPage = Math.max(
      1,
      Math.ceil(
        productCount / productsPerPage
      )
    );

    const pageAmount = Math.min(
      maxPage,
      Math.max(
        4,
        Math.min(
          7,
          Math.ceil(displayCount / 2)
        )
      )
    );

    const pages = choosePages(
      maxPage,
      pageAmount
    );

    const groups = await Promise.all(
      pages.map((page) =>
        fetchPageCards(
          collectionUrl,
          page
        ).catch((error) => {
          console.warn(
            '[Popular cards]',
            error
          );

          return [];
        })
      )
    );

    return chooseCards(
      groups,
      displayCount
    ).map(createSlide);
  }

  function initialize(root) {
    if (!root || initialized.has(root)) {
      return;
    }

    const viewport = root.querySelector(
      '[data-cn-popular-viewport]'
    );

    const track = root.querySelector(
      TRACK_SELECTOR
    );

    const loading = root.querySelector(
      '[data-cn-popular-loading]'
    );

    if (!viewport || !track) return;

    initialized.add(root);

    trackStates.set(track, {
      frame: 0
    });

    setBooleanAttribute(
      root,
      'data-can-scroll-left',
      false
    );

    setBooleanAttribute(
      root,
      'data-can-scroll-right',
      false
    );

    track.addEventListener(
      'scroll',
      () => scheduleScrollState(root, track),
      { passive: true }
    );

    loadRandomCards(root)
      .then((slides) => {
        /*
          Alle DOM-Schreibvorgänge werden in einem Schritt durchgeführt.
          Anschließend erfolgt genau eine Geometriemessung.
        */
        track.replaceChildren(...slides);
        track.scrollLeft = 0;

        viewport.classList.remove('is-loading');
        viewport.setAttribute(
          'aria-busy',
          'false'
        );

        loading?.remove();

        scheduleScrollState(root, track);

        document.dispatchEvent(
          new CustomEvent(
            'cn:popular-cards:ready',
            { detail: { root } }
          )
        );
      })
      .catch((error) => {
        console.error(
          '[Popular cards]',
          error
        );

        if (loading) {
          loading.textContent =
            'Die Karten konnten nicht geladen werden.';
        }

        viewport.setAttribute(
          'aria-busy',
          'false'
        );
      });
  }

  function initializeAll(scope = document) {
    scope
      .querySelectorAll(ROOT_SELECTOR)
      .forEach(initialize);
  }

  let resizeFrame = 0;

  window.addEventListener(
    'resize',
    () => {
      if (resizeFrame) return;

      resizeFrame =
        requestAnimationFrame(() => {
          resizeFrame = 0;

          document
            .querySelectorAll(ROOT_SELECTOR)
            .forEach((root) => {
              const track =
                root.querySelector(
                  TRACK_SELECTOR
                );

              if (track) {
                scheduleScrollState(
                  root,
                  track
                );
              }
            });
        });
    },
    { passive: true }
  );

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => initializeAll(),
      { once: true }
    );
  } else {
    initializeAll();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => initializeAll(event.target)
  );
})();
